import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import type { CalendarEventItem } from "@/types/task";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Calendar,
  Clock,
  Video,
  MapPin,
  Users,
  Send,
  CheckCircle2,
  Trash2,
  ExternalLink,
  RotateCcw,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { useConfirm } from "@/context/ConfirmContext";

interface TaskDetailSheetProps {
  event: CalendarEventItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated?: () => void;
  onDeleted?: () => void;
}

export function TaskDetailSheet({
  event,
  open,
  onOpenChange,
  onUpdated,
  onDeleted,
}: TaskDetailSheetProps) {
  const { confirm } = useConfirm();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (!event) return null;

  const isExternal = event.kind === "EXTERNAL";
  const start = new Date(event.start);
  const end = new Date(event.end);

  const formattedDate = start.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const formattedTime = event.all_day
    ? "All Day Event"
    : `${start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} – ${end.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;

  const handleResendInvite = async () => {
    setActionLoading("invite");
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      const res = await api.post(`/api/Task/Invite/${event.id}`, {});
      unwrapObject(res);
      setSuccessMsg("Meeting invitation (.ics) successfully dispatched!");
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to send meeting invite");
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleComplete = async () => {
    setActionLoading("complete");
    setErrorMsg(null);
    try {
      if (event.task_status === "DONE") {
        await api.patch(`/api/Task/Reopen/${event.id}`, {});
      } else {
        await api.patch(`/api/Task/Complete/${event.id}`, {});
      }
      onUpdated?.();
      onOpenChange(false);
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to update task status");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!(await confirm({ title: `Delete "${event.title}"?`, confirmLabel: "Delete", destructive: true }))) return;
    setActionLoading("delete");
    try {
      await api.delete(`/api/Task/Delete/${event.id}`);
      onDeleted?.();
      onOpenChange(false);
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to delete task");
    } finally {
      setActionLoading(null);
    }
  };

  const getEntityUrl = () => {
    if (!event.related_type || !event.related_id) return null;
    switch (event.related_type) {
      case "LEAD":
        return `/leads/${event.related_id}`;
      case "DEAL":
        return `/deals/${event.related_id}`;
      case "ACCOUNT":
        return `/accounts/${event.related_id}`;
      case "CONTACT":
        return `/contacts/${event.related_id}`;
      case "QUOTE":
        return `/quotes/${event.related_id}`;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl md:max-w-2xl">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant="outline"
              className={`text-xs font-bold tracking-wider ${
                isExternal
                  ? "bg-purple-50 text-purple-700 border-purple-200"
                  : event.task_type === "MEETING"
                  ? "bg-blue-50 text-blue-700 border-blue-200"
                  : "bg-primary/10 text-primary border-primary/20"
              }`}
            >
              {isExternal ? `${event.provider} CALENDAR` : event.task_type || "TASK"}
            </Badge>

            {event.priority && (
              <Badge
                variant="outline"
                className={`text-xs font-semibold ${
                  event.priority === "HIGH"
                    ? "bg-red-50 text-red-700 border-red-200"
                    : event.priority === "LOW"
                    ? "bg-slate-50 text-slate-700 border-slate-200"
                    : "bg-sky-50 text-sky-700 border-sky-200"
                }`}
              >
                {event.priority}
              </Badge>
            )}

            {event.task_status === "DONE" && (
              <Badge
                variant="outline"
                className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200"
              >
                DONE
              </Badge>
            )}
          </div>

          <DialogTitle className="text-xl sm:text-2xl font-bold mt-2 leading-snug">
            {event.title}
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
            {formattedDate} &bull; {formattedTime}
          </DialogDescription>
        </DialogHeader>

        {successMsg && (
          <div className="p-3.5 rounded-lg bg-emerald-50 text-emerald-700 text-xs sm:text-sm border border-emerald-200 flex items-center gap-2">
            <CheckCircle2 className="size-4 shrink-0" />
            <span>{successMsg}</span>
          </div>
        )}

        {errorMsg && (
          <div className="p-3.5 rounded-lg bg-red-50 text-red-700 text-xs sm:text-sm border border-red-200 flex items-center gap-2">
            <AlertCircle className="size-4 shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}

        <div className="space-y-4 py-1 text-sm">
          {/* Video meeting link */}
          {event.online_link && (
            <div className="p-3.5 rounded-xl bg-primary/5 border border-primary/20 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0">
                  <Video className="size-5" />
                </div>
                <div>
                  <div className="font-semibold text-sm text-foreground">
                    {event.meeting_provider ? event.meeting_provider.replace("_", " ") : "Video Meeting"}
                  </div>
                  <div className="text-xs text-muted-foreground truncate max-w-[280px] sm:max-w-md">
                    {event.online_link}
                  </div>
                </div>
              </div>
              <Button size="sm" asChild className="text-xs sm:text-sm h-9">
                <a href={event.online_link} target="_blank" rel="noreferrer">
                  Join Meeting
                </a>
              </Button>
            </div>
          )}

          {/* Location */}
          {event.location && (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <MapPin className="size-4 text-foreground shrink-0" />
              <span className="text-foreground">{event.location}</span>
            </div>
          )}

          {/* Linked entity */}
          {event.related_name && (
            <div className="p-3 rounded-lg border border-border bg-card/60 flex items-center justify-between text-sm">
              <span className="text-muted-foreground text-xs sm:text-sm">
                Linked {event.related_type?.toLowerCase()}:
              </span>
              {getEntityUrl() ? (
                <Link
                  to={getEntityUrl()!}
                  className="font-semibold text-primary hover:underline flex items-center gap-1.5"
                >
                  {event.related_name}
                  <ExternalLink className="size-3.5" />
                </Link>
              ) : (
                <span className="font-semibold text-foreground">{event.related_name}</span>
              )}
            </div>
          )}

          {/* Assignee */}
          {event.assigned_to && (
            <div className="text-muted-foreground text-xs sm:text-sm flex items-center gap-2">
              <span>Owner:</span>
              <span className="font-semibold text-foreground">
                {[event.assigned_to.first_name, event.assigned_to.last_name]
                  .filter(Boolean)
                  .join(" ") || event.assigned_to.email}
              </span>
            </div>
          )}

          {/* Attendees */}
          {event.attendees && event.attendees.length > 0 && (
            <div className="space-y-2 pt-1">
              <div className="font-semibold text-xs sm:text-sm text-foreground flex items-center gap-1.5">
                <Users className="size-4 text-muted-foreground" />
                <span>Attendees ({event.attendees.length})</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {event.attendees.map((att, i) => (
                  <span
                    key={i}
                    className="px-2.5 py-1 rounded-md bg-muted text-xs font-medium border border-border text-foreground"
                  >
                    {att.name || att.email}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          {event.description && (
            <div className="space-y-1.5 pt-1">
              <div className="font-semibold text-xs sm:text-sm text-foreground">Notes & Agenda</div>
              <p className="text-muted-foreground text-sm whitespace-pre-wrap leading-relaxed bg-muted/30 p-3 rounded-lg border border-border/60">
                {event.description}
              </p>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between pt-3 border-t border-border mt-1">
          {!isExternal ? (
            <>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDelete}
                  disabled={actionLoading === "delete"}
                  className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
                >
                  <Trash2 className="size-3.5 mr-1" /> Delete
                </Button>

                {event.task_type === "MEETING" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResendInvite}
                    disabled={actionLoading === "invite"}
                    className="text-xs"
                    title="Resend RFC 5545 invite email (.ics)"
                  >
                    {actionLoading === "invite" ? (
                      <Loader2 className="size-3.5 animate-spin mr-1" />
                    ) : (
                      <Send className="size-3.5 mr-1" />
                    )}
                    Send Invite
                  </Button>
                )}
              </div>

              <Button
                size="sm"
                onClick={handleToggleComplete}
                disabled={actionLoading === "complete"}
                className={`text-xs ${
                  event.task_status === "DONE"
                    ? "bg-muted text-muted-foreground hover:bg-muted/80"
                    : "bg-emerald-600 hover:bg-emerald-700 text-white"
                }`}
              >
                {event.task_status === "DONE" ? (
                  <>
                    <RotateCcw className="size-3.5 mr-1" /> Reopen
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="size-3.5 mr-1" /> Mark Done
                  </>
                )}
              </Button>
            </>
          ) : (
            <div className="flex items-center justify-between w-full">
              <span className="text-[11px] text-muted-foreground">
                Synced from external provider (read-only)
              </span>
              {event.provider_url && (
                <Button size="sm" variant="outline" asChild className="text-xs">
                  <a href={event.provider_url} target="_blank" rel="noreferrer">
                    <ExternalLink className="size-3.5 mr-1" /> Open in {event.provider}
                  </a>
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
